/** @jsxImportSource @emotion/react */
import { PanelCard } from '../../../design-system/molecules/PanelCard/PanelCard';
import { Badge } from '../../../design-system/atoms/Badge/Badge';
import { Button } from '../../../design-system/atoms/Button/Button';
import { Select } from '../../../design-system/atoms/Select/Select';
import { Switch } from '../../../design-system/atoms/Switch/Switch';
import { CodeBlock } from '../../../design-system/atoms/CodeBlock/CodeBlock';
import { Cluster } from '../../../design-system/layout/Cluster';
import type { ModuleConfig } from '../../types';
import {
  stubStyle,
  headStyle,
  nameStyle,
  descStyle,
  fieldsStyle,
  fieldLabelStyle,
  monoStyle,
  jsonBlockStyle,
} from './ModuleConfigCard.styles';

export interface ModuleConfigCardProps {
  module: ModuleConfig;
  onEdit?: () => void;
  onRunNow?: () => void;
  /** "지금 수집" 활성화 여부. false면 버튼 비활성(현재 주식 모듈만 지원). */
  runNowEnabled?: boolean;
  onToggleEnabled?: (checked: boolean) => void;
  onChangeCollector?: (value: string) => void;
}

/** 모듈 설정 카드. module_config 한 건의 스케줄·수집기·기본 입력 표시에 활용. */
export function ModuleConfigCard({
  module,
  onEdit,
  onRunNow,
  runNowEnabled = false,
  onToggleEnabled,
  onChangeCollector,
}: ModuleConfigCardProps) {
  return (
    <div css={module.stub ? stubStyle : undefined}>
      <PanelCard>
        <div css={headStyle}>
          <div>
            <Cluster gap="sm">
              <h2 css={nameStyle}>{module.name}</h2>
              <Badge tone={module.enabled ? 'success' : 'neutral'}>
                {module.enabled ? 'enabled' : 'disabled · 스텁'}
              </Badge>
            </Cluster>
            <p css={descStyle}>{module.description}</p>
          </div>
          <Cluster gap="sm">
            <Button variant="outline" tone="neutral" disabled={module.stub} onClick={onEdit}>
              수정
            </Button>
            {!module.stub && (
              <Button onClick={onRunNow} disabled={!runNowEnabled}>
                ▶ 지금 수집
              </Button>
            )}
          </Cluster>
        </div>

        {!module.stub && (
          <>
            <div css={fieldsStyle}>
              <div>
                <div css={fieldLabelStyle}>cron_expr</div>
                <div css={monoStyle}>{module.cronExpr}</div>
              </div>
              <div>
                <div css={fieldLabelStyle}>collector_type</div>
                <Select
                  options={(module.collectorOptions ?? []).map((o) => ({ label: o, value: o }))}
                  value={module.collectorType}
                  onChange={(v) => onChangeCollector?.(v)}
                />
              </div>
              <div>
                <div css={fieldLabelStyle}>next_run_at</div>
                <div>{module.nextRunAt}</div>
              </div>
              <div>
                <div css={fieldLabelStyle}>enabled</div>
                <Switch checked={module.enabled} onChange={onToggleEnabled} />
              </div>
            </div>

            <div css={jsonBlockStyle}>
              <div css={fieldLabelStyle}>input_json (cron 트리거 기본값)</div>
              <CodeBlock>{module.inputJson}</CodeBlock>
            </div>
          </>
        )}
      </PanelCard>
    </div>
  );
}
